import * as React from 'react';
import { Typography } from '@components/basics/typography';
import {
  ElementsConsumer,
  Elements,
  PaymentElement,
} from '@stripe/react-stripe-js';
import {
  PaymentIntent,
  PaymentMethod,
  Stripe,
  StripeElements,
  loadStripe,
} from '@stripe/stripe-js';
import styled from 'styled-components';

// SDK
import * as payment from '@webapp/sdk/payment';

// Types
import { PaymentElementOptions } from '@webapp/types/stripe/payment-element-options';

// Components
import Box from '@components/layout/box';
import { CreatePaymentIntentRequest } from '@services/payment/types/api/create-payment-intent-req';
import { CreateNewStripeSubscriptionReq } from '@webapp/types/stripe/create-new-stripe-subscription-req';
import { createTrialSubscription } from '@webapp/sdk/stripe/create-trial-subscription';
import { Subscription } from '@services/subscription/types/subscription';

interface StripePaymentCtrlProps {
  isMobile: boolean;
  paymentOptions: PaymentElementOptions;
  paymentIntent?: CreatePaymentIntentRequest;
  subscriptionRequest?: CreateNewStripeSubscriptionReq;
  paymentBtnRef: React.MutableRefObject<HTMLButtonElement>;
  redirectUrl?: string;
  onError: () => void;
  onSuccess: (
    paymentMethod?: PaymentMethod,
    stripe?: Stripe,
    intent?: PaymentIntent,
    subscription?: Subscription
  ) => void;
}

const ELEMENTS_OPTIONS = {
  fonts: [
    {
      cssSrc:
        'https://fonts.googleapis.com/css2?family=Sen:wght@400;700&display=swap',
    },
  ],
};

class StripePayment extends React.Component<{
  stripe: Stripe | null;
  elements: StripeElements | null;
  paymentIntent?: CreatePaymentIntentRequest;
  subscriptionRequest?: CreateNewStripeSubscriptionReq;
  paymentOptions: PaymentElementOptions;
  paymentBtnRef: React.MutableRefObject<HTMLButtonElement>;
  redirectUrl?: string;
  onSuccess: (
    paymentMethod?: PaymentMethod,
    stripe?: Stripe,
    intent?: PaymentIntent,
    subscription?: Subscription
  ) => void;
  onError: (error: string) => void;
}> {
  handleSubmit = async (event) => {
    event.preventDefault();

    const { stripe, elements, onError, subscriptionRequest } = this.props;

    if (!stripe || !elements) {
      return;
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      onError(
        submitError.message || 'An error occured during payment processing...'
      );
      return;
    }
    if (
      this.props.paymentOptions.mode === 'subscription' &&
      subscriptionRequest
    ) {
      const { type, clientSecret, subscription } =
        await createTrialSubscription(subscriptionRequest);
      if (type === 'setup') {
        const { error } = await stripe.confirmSetup({
          elements,
          clientSecret,
          confirmParams: {
            return_url: this.props.redirectUrl as string,
          },
          redirect: 'if_required',
        });

        if (error) {
          onError('An error occured during payment processing...');
          return;
        }
      }

      if (type === 'payment') {
        const { error, paymentIntent } = await stripe.confirmPayment({
          elements,
          clientSecret,
          confirmParams: {
            return_url: this.props.redirectUrl as string,
          },
          redirect: 'if_required',
        });
        if (error || paymentIntent.status !== 'succeeded') {
          onError('An error occured during payment processing...');
          return;
        }
      }
      this.props.onSuccess(undefined, stripe, undefined, subscription);
    }

    if (this.props.paymentIntent) {
      const paymentIntent = await payment.createPaymentIntent(
        this.props.paymentIntent
      );

      if (!paymentIntent.client_secret) {
        onError('An error occured during payment processing...');
        return;
      }
      const { error } = await stripe.confirmPayment({
        elements,
        clientSecret: paymentIntent.client_secret,
        confirmParams: {
          return_url: this.props.redirectUrl || '',
        },
        redirect: 'always',
      });

      if (error || paymentIntent.status !== 'succeeded') {
        onError('An error occured during payment processing...');
        return;
      }
    }
  };

  render() {
    return (
      <form onSubmit={this.handleSubmit}>
        <PaymentElement />
        <button ref={this.props.paymentBtnRef} hidden={true}></button>
      </form>
    );
  }
}

export default function InjectedPaymentForm(props: StripePaymentCtrlProps) {
  const [message, setMessage] = React.useState('');

  const stripePromise = React.useMemo(
    () => loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY as string),
    []
  );

  return (
    <BoxWrapper>
      {message ? (
        <ErrorTypography type="small" weight="bold" className="mb-20">
          {message}
        </ErrorTypography>
      ) : null}
      <Box noBorderTop={props.isMobile} noBorderBottom={props.isMobile}>
        <Elements
          stripe={stripePromise}
          options={{ ...props.paymentOptions, ...ELEMENTS_OPTIONS }}
        >
          <ElementsConsumer>
            {({ stripe, elements }) => (
              <StripePayment
                stripe={stripe}
                elements={elements}
                onError={(error) => {
                  setMessage(error);
                  props.onError();
                }}
                paymentBtnRef={props.paymentBtnRef}
                paymentIntent={props.paymentIntent}
                redirectUrl={props.redirectUrl}
                onSuccess={props.onSuccess}
                subscriptionRequest={props.subscriptionRequest}
                paymentOptions={props.paymentOptions}
              />
            )}
          </ElementsConsumer>
        </Elements>
      </Box>
    </BoxWrapper>
  );
}

const BoxWrapper = styled.div`
  display: grid;
  grid-gap: 3px;
`;

const ErrorTypography = styled(Typography)`
  color: ${({ theme }) => theme.colors.error};
`;
